import { Observable } from 'rxjs';
import { CreateLibraryCommand } from '@model/library/commands/library-create-command';

/**
 * Response from creating a library
 */
export interface CreateLibraryResponse {
  libraryId: string;
  cellId: string;
  plateId: string;
  indexId: string;
  sampleCount: number;
  createdAt: Date;
  createdByUserId: string;
}

/**
 * Raw input format for library creation.
 * Allows legacy callers to pass untyped library data that will be normalized
 * to CreateLibraryCommand internally by the use case / adapter.
 */
export interface RawCreateLibraryInput {
  cellId?: string;
  technology: string;
  samples: {
    lgCode: string;
    indexName: string;
    indexAdapterId?: string;
    sequence?: string;
    uploadMethod: string;
    position?: string;
  }[];
  libraryGroups?: {
    libraryCode?: string;
    chipType?: string;
    arrangementType?: string;
    plateName?: string;
    chipLot?: string;
    chipReference?: string;
    creationDate?: string;
    lot?: string;
    reference?: string;
    equipment?: string;
    samples: {
      lgCode: string;
      indexName: string;
      indexAdapterId?: string;
      sequence?: string;
      uploadMethod: string;
      position?: string;
    }[];
  }[];
}

/**
 * Input type that accepts both raw and typed command formats.
 * This allows gradual migration from raw input to typed commands.
 */
export type CreateLibraryInput = CreateLibraryCommand | RawCreateLibraryInput;

/**
 * Library Repository
 *
 * Defines the contract for library operations.
 * This abstract class is defined in the domain layer and implemented in the infrastructure layer.
 */
export abstract class LibraryRepository {
  /**
   * Create a library with samples
   *
   * @param input - Command or legacy request containing library data
   * @returns Observable with created library information
   */
  abstract createLibrary(
    input: CreateLibraryInput
  ): Observable<CreateLibraryResponse>;
}
